<template>
  <v-list-item
    :value="path"
    :style="{ paddingLeft: `${depth * 16 + 4}px !important` }"
    class="file-tree-item"
  >
    <template #title>
      <div class="d-flex align-center">
        <v-tooltip
          v-if="emptyReadModelToolTip"
          :text="emptyReadModelToolTip"
        >
          <template #activator="{ props }">
            <span v-bind="props">{{ displayName || name }}</span>
          </template>
        </v-tooltip>
        <span v-else>{{ displayName || name }}</span>
        <v-tooltip
          v-if="readModelMergeInfo"
          location="top"
          max-width="350px"
          text="Read Models are automatically merged into a single API endpoint if they query the same entity with
           the same cardinality (e.g., fetching a single item vs. a list of items)."
        >
          <template #activator="{ props }">
            <span v-bind="props" class="text-caption text-medium-emphasis ml-2">
              ({{ readModelMergeInfo.original }} merged to {{ readModelMergeInfo.merged }})
            </span>
          </template>
        </v-tooltip>
      </div>
    </template>
    <template #prepend>
      <v-icon v-if="item.isFolder" size="16">{{ item.isOpened ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
      <v-icon v-else-if="!isDomainItem" size="16">mdi-file-outline</v-icon>
      <v-icon v-if="isDomainItem" size="16" class="pl-0" :color="itemColor">
        {{ itemIcon }}
      </v-icon>
    </template>
    <template #append>
      <div class="d-flex align-center ga-1 pl-2">
        <v-tooltip v-if="isDomainDirty">
          <template #activator="{ props }">
            <span v-bind="props" class="domain-change-indicator"></span>
          </template>
          <span>Domain changes detected</span>
        </v-tooltip>
        <v-tooltip v-else-if="isDirty">
          <template #activator="{ props }">
            <v-icon v-bind="props" class="small" color="#FBD505">mdi-circle</v-icon>
          </template>
          <span>Code changes detected</span>
        </v-tooltip>
        <v-menu v-if="depth < 3" location="bottom" :offset="0">
          <template #activator="{ props }">
            <v-btn
              v-if="!displayName"
              class="show-on-hover-item"
              v-bind="props"
              size="small"
              variant="plain"
              tabindex="-1"
              icon="mdi-dots-vertical"
              :style="{ 'cursor': 'pointer' }"
            />
          </template>
          <template #default>
            <v-list density="compact" class="pt-0">
              <template v-for="item in menuItems" :key="item.label">
                <v-tooltip
                  v-if="item.disabled && item.tooltip"
                  location="right"
                  max-width="400px"
                >
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        class="py-0 min-h-0"
                        :disabled="item.disabled"
                        style="pointer-events: none;"
                      >
                        <v-list-item-title class="text-sm d-flex align-center ga-1">
                          <v-icon size="16" class="pa-0">{{ item.icon }}</v-icon>
                          <span>{{ item.label }}</span>
                        </v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span>{{ item.tooltip }}</span>
                </v-tooltip>
                <v-list-item
                  v-else
                  class="py-0 min-h-0"
                  @click="item.action"
                  :disabled="item.disabled"
                >
                  <v-list-item-title class="text-sm d-flex align-center ga-1">
                    <v-icon size="16" class="pa-0">{{ item.icon }}</v-icon>
                    <span>{{ item.label }}</span>
                  </v-list-item-title>
                  <v-tooltip
                    v-if="item.tooltip"
                    activator="parent"
                    location="right"
                    max-width="400px"
                  >
                    <span>
                      {{ item.tooltip }}
                    </span>
                  </v-tooltip>
                </v-list-item>
              </template>
            </v-list>
          </template>
        </v-menu>
      </div>
      <!-- <v-tooltip v-if="!isFileEmpty" location="top" open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon v-if="!!object?.draftContents" class="small" color="warning" v-bind="props">mdi-circle</v-icon>
          <v-icon v-else class="small" color="success" v-bind="props">mdi-check-circle</v-icon>
        </template>
        <span>{{ !!object?.draftContents ? 'Unsaved changes' : 'Up to date' }}</span>
      </v-tooltip> -->
    </template>
  </v-list-item>
</template>

<script setup>
import { computed, inject } from 'vue'
import { useStore } from 'vuex'
import {
  CODE_EDITOR_APPLY_PATCH,
  CODE_EDITOR_GENERATE_AGAIN,
  CODE_EDITOR_SHOW_DOMAIN_CHANGES,
  CODE_EDITOR_EDIT_PROMPT,
  CODE_EDITOR_COPY_PROMPT
} from '@/codegen/constants'

const store = useStore()

const {
  item,
  domainObjects,
  isTemplateSelected,
  readModelMergeInfo
} = defineProps({
  item: { type: Object },
  domainObjects: { type: Object },
  isTemplateSelected: { type: Boolean },
  readModelMergeInfo: {
    type: Object,
    default: null
  }
})

const { displayName, name, path, depth } = item

const isDomainDirty = computed(() => !!domainObjects[path]?.isDomainDirty)

const isEntityReady = computed(() => !!domainObjects[path]?.isEntityReady)

const domainType = computed(() => domainObjects[path]?.diff?.domainType)

const tooltipMessage = computed(() => {
  if (!isTemplateSelected) {
    return 'Please select a framework boilerplate before generating code'
  }

  if (depth === 0) return ''
  if (depth === 1 && !isEntityReady.value && name !== 'entity') return 'Entity code must be generated first'
  if (depth === 2 && !isEntityReady.value && domainType.value !== 'entity') {
    return 'Entity code must be generated first'
  }

  return ''
})

const childDomainKeys = Object.keys(domainObjects).filter(key => key.startsWith('domain-model/readmodel') && key.split('/').length === 3)
const emptyReadModelToolTip = computed(() => depth === 1 && name === 'readmodel' && !childDomainKeys.length ? 'Shows only read models with selected entities.' : '')

const icons = { command: 'mdi-flash', readmodel: 'mdi-magnify', entity: 'mdi-shape-outline', gwt: 'mdi-timeline' }
const colors = { command: 'blue-lighten-1', readmodel: 'green-lighten-1', entity: 'amber-darken-1', gwt: 'purple-lighten-1' }
const itemIcon = computed(() => icons[path.split('/')[1]])
const itemColor = computed(() => colors[path.split('/')[1]])

const isDomainItem = computed(() => depth === 2)

const isDirty = computed(() => {
  const keys = Object.keys(domainObjects)
  const filteredKeys = keys.filter((key) => key.startsWith(path))

  return filteredKeys.some((key) => domainObjects[key].draftContents != null)
})

const treeAction = inject('treeAction', (data) => {
  console.warn('doDomainAction not provided', data)
})

const menuItems = computed(() => {
  const keys = Object.keys(domainObjects)
  const filteredDomains = keys.filter((key) => key.startsWith(path) && key.split('/').length === 3)
  const isFullyGenerated = filteredDomains.every(_domain => keys.filter((key) => key.startsWith(_domain) && key.split('/').length > 3).length > 0)

  const res = [
    {
      label: 'Apply Patch',
      icon: 'mdi-file-compare',
      disabled: store.state.isWorkflowDisabled || !isDirty.value,
      action: () => treeAction({ type: CODE_EDITOR_APPLY_PATCH, path, name })
    },
    {
      label: !isFullyGenerated ? 'Generate' : 'Regenerate',
      icon: 'mdi-reload',
      disabled: store.state.isWorkflowDisabled || tooltipMessage.value !== '',
      tooltip: tooltipMessage.value,
      action: () => treeAction({ type: CODE_EDITOR_GENERATE_AGAIN, path, name, item })
    },
    {
      label: 'View Changes',
      icon: 'mdi-eye',
      disabled: !isDomainDirty.value,
      action: () => treeAction({ type: CODE_EDITOR_SHOW_DOMAIN_CHANGES, path, name, item })
    },
    {
      label: 'Edit Prompt',
      icon: 'mdi-pencil',
      disabled: store.state.isWorkflowDisabled,
      action: () => treeAction({ type: CODE_EDITOR_EDIT_PROMPT, path, name })
    }
  ]

  if (isDomainItem.value) {
    res.push({
      label: 'Copy Final Prompt',
      icon: 'mdi-content-copy',
      action: () => treeAction({ type: CODE_EDITOR_COPY_PROMPT, path, name })
    })
  }

  return res
})
</script>

<style scoped>
v-list {
  --prepend-width: 16px;
}
</style>

<style scoped>
.file-tree-group {
  --prepend-width: 0px !important;
  /* padding-inline-start: var(--indent-padding) !important; */
}

.v-list > .file-tree-group {
  --prepend-width: -16px !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px !important;
  border-radius: 4px !important;
}

.file-tree-item {
  .show-on-hover-item {
    /* display: none !important; */
    opacity: 0 !important;
  }

  &:hover {
    .show-on-hover-item {
      /* display: block !important; */
      opacity: 1 !important;
    }
  }
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline: 4px !important;
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line .file-tree-item {
  padding-inline: 16px !important;
}

.v-list-item--density-compact.v-list-item--one-line {
  min-height: 28px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}

.file-tree-item {
  padding: 0 4px !important;
  height: 24px !important;
  min-height: 24px !important;
  font-size: 12px !important;
  padding-inline-start: var(--indent-padding) !important;
  /* padding-inline-start: 16px !important; */
  /* --indent-padding: calc(8px + var(--indent-padding)); */
  /* padding-inline-start: calc(2px + var(--indent-padding)) !important; */
}

.file-tree-item .v-btn {
  width: 20px;
  height: 20px;
}

.file-tree-item .v-icon {
  font-size: 20px;
  padding-left: 0px;
}

.file-tree-item .v-icon.small {
  padding-left: 0px;
  font-size: 10px;
}

.file-tree-item .v-list-item__prepend {
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  height: 24px;
}

.file-tree-item .v-list-item__append {
  width: 28px;
  min-width: 28px;
  max-width: 28px;
}

.domain-change-indicator {
  width: 8px;
  height: 8px;
  background-color: #ff5252;
  border-radius: 50%;
  box-shadow: 0 0 0 rgba(255, 82, 82, 0.7);
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 82, 82, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
  }
}
</style>
